import { useCallback, useEffect, useRef, useState } from "react";
import { ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, imgSrc } from "@/lib/api";
import { compress } from "./ImageUpload";
import { Field, FormDialog, RowActions, field } from "./FormBits";

export const GalleryTab = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState("");
  const input = useRef(null);
  const load = useCallback(() => { api.get("/gallery").then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const uploadMany = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    let ok = 0;
    for (const [n, file] of files.entries()) {
      setBusy(`Uploading ${n + 1} of ${files.length}…`);
      try {
        const fd = new FormData();
        fd.append("file", await compress(file), "photo.jpg");
        const { data } = await api.post("/admin/upload", fd);
        await api.post("/admin/gallery", { image_url: data.url, order: 0 });
        ok += 1;
      } catch (err) { toast.error(`${file.name}: ${errMsg(err)}`); }
    }
    setBusy("");
    if (ok) toast.success(`${ok} photo${ok > 1 ? "s" : ""} added. Tap Edit to add artist names.`);
    load();
  };
  const save = async (e) => {
    e.preventDefault();
    const { id, created_at, ...body } = form;
    body.order = Number(body.order) || 0;
    try { await api.put(`/admin/gallery/${id}`, body); toast.success("Saved"); setForm(null); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (it) => {
    if (!window.confirm("Delete this photo from the gallery?")) return;
    await api.delete(`/admin/gallery/${it.id}`).catch(() => {}); toast.success("Deleted"); load();
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div>
      <input ref={input} type="file" accept="image/*" multiple onChange={uploadMany} className="hidden" data-testid="admin-gallery-input" />
      <button data-testid="admin-gallery-add-btn" disabled={!!busy} onClick={() => input.current?.click()} className="mb-4 inline-flex items-center gap-2 bg-[#56ED4A] text-black px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] font-semibold disabled:opacity-60">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} {busy || "Add photos"}
      </button>
      {!rows.length && <p data-testid="admin-gallery-empty" className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 mb-4">No photos yet. Tap “Add photos” and pick as many as you like.</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {rows.map((it) => (
          <div key={it.id} data-testid={`admin-gallery-${it.id}`} className="border border-white/10 bg-[#121216]">
            <div className="aspect-square bg-black"><img src={imgSrc(it.image_url)} alt="" className="w-full h-full object-contain" /></div>
            <div className="p-2">
              <p className="font-display uppercase text-lg truncate">{it.artists || "No artist yet"}</p>
              <p className="font-mono text-[11px] text-zinc-400 truncate mb-2">{it.event || "—"}</p>
              <RowActions testId={`admin-gallery-${it.id}`} onEdit={() => setForm({ ...it })} onDelete={() => del(it)} />
            </div>
          </div>
        ))}
      </div>
      {form && (
        <FormDialog open onOpenChange={(o) => !o && setForm(null)} title="Edit photo" onSubmit={save} testId="gallery-form">
          <div className="sm:col-span-2 bg-black"><img src={imgSrc(form.image_url)} alt="" className="w-full max-h-64 object-contain" /></div>
          <Field label="Artists / people in photo"><input data-testid="gallery-form-artists" value={form.artists} onChange={set("artists")} placeholder="e.g. Jay-B Real w/ special guest" className={field} /></Field>
          <Field label="Show / event"><input data-testid="gallery-form-event" value={form.event} onChange={set("event")} placeholder="e.g. Warehouse Sessions · Oakland 2025" className={field} /></Field>
          <div className="sm:col-span-2"><Field label="Caption"><textarea data-testid="gallery-form-caption" rows={2} value={form.caption} onChange={set("caption")} className={field} /></Field></div>
          <Field label="Order (lower shows first)"><input data-testid="gallery-form-order" type="number" value={form.order} onChange={set("order")} className={field} /></Field>
        </FormDialog>
      )}
    </div>
  );
};
